import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ThemeProvider from "../../styles/theme/theme-provider";
import yup from "../../utils/yup";
import { useBrandTones as useBrandTonesQuery } from "../brand-kit/hooks/use-brand-tones";
import { BrandTone } from "../brand-kit/server/brand-tone-service";
import { CeDropdown } from "../generic/components/form-fields/ce-dropdown";
import { CeTextInput } from "../generic/components/form-fields/ce-text-field";
const useBrandToneOptions = (): { label: string; value: string; default?: boolean }[] => {
  const [brandTones, setBrandTones] = useState<BrandTone[]>([]);
  const { data, loading } = useBrandTonesQuery();

  useEffect(() => {
    if (data && !loading) {
      setBrandTones(data);
    }
  }, [data, loading]);

  const defaultToneId = useMemo(
    () => (brandTones.length === 1 ? brandTones[0].id : brandTones.find((x) => x.is_default)?.id),
    [brandTones],
  );

  return brandTones.map((brandTone) => ({
    label: brandTone.name,
    value: brandTone.id,
    default: brandTone.id === defaultToneId,
  }));
};

const useContentFormatOptions = (): { label: string; value: string }[] => {
  const { t } = useTranslation();
  return [
    { label: t("Moment"), value: "moment" },
    { label: t("Poster"), value: "poster" },
    { label: t("Zoom Background"), value: "zoom_background" },
    { label: t("Employee Recognition"), value: "employee_recognition" },
    { label: t("Certificate"), value: "certificate" },
    { label: t("FAQ"), value: "faq" },
    { label: t("TV Display"), value: "tv_display" },
    { label: t("LinkedIn Cover"), value: "linkedin_cover" },
    { label: t("Manager One Pager"), value: "manager_one_pager" },
    { label: t("Logo"), value: "logo" },
    { label: t("Gift Card"), value: "gift_card" },
  ];
};

type CreateContentRequest = {
  q: string;
  format: string;
  tone?: string;
};

export const CreateContentCard: FC = () => {
  const { t } = useTranslation();

  const brandToneOptions = useBrandToneOptions();

  const schema = useMemo(() => {
    return yup.object({
      q: yup.string().required(t("Please describe the content you wish to create.")),
      tone: yup
        .string()
        .oneOf(brandToneOptions.map((option) => option.value))
        .optional(),
    });
  }, [brandToneOptions, t]);

  const contentFormatOptions = useContentFormatOptions();

  const methods = useForm<CreateContentRequest>({
    defaultValues: { q: "", format: "moment" },
    resolver: yupResolver(schema),
  });

  const { watch, setValue } = methods;

  const format = watch("format");
  const tone = watch("tone");

  useEffect(() => {
    if (brandToneOptions.length === 0) return;

    if (tone === undefined) {
      setValue("tone", brandToneOptions.find((x) => x.default)?.value);
    }
  }, [brandToneOptions, setValue, tone]);

  const handleSubmit = useCallback((request: CreateContentRequest) => {
    console.log(request);
  }, []);

  return (
    <FormProvider {...methods}>
      <ThemeProvider mode="dark">
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <Card
            raised
            sx={{
              backgroundColor: "rgba(32, 42, 56)",
              backgroundPosition: "right bottom, left top",
              backgroundRepeat: "no-repeat",
              backgroundImage:
                'url("https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/8e077b68-e73f-4d25-b5ba-b13f1c428f37"), url("https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/557ee693-e504-48cc-b184-5d8ef8161111")',
            }}
          >
            <CardContent sx={{ m: 12 }}>
              <Typography variant="h5">{t("Create Content Instantly with AI!")}</Typography>
              <Typography variant="body2" mt={1}>
                {t("Let our powerful AI generate complete content for you in seconds.")}
              </Typography>
              <Grid container spacing={4} mt={2}>
                <Grid item xs={6}>
                  <Stack gap={2}>
                    <CeDropdown<CreateContentRequest>
                      label="Content Type"
                      name="format"
                      options={contentFormatOptions}
                      InputProps={{ variant: "standard" }}
                    />
                    {format === "moment" && (
                      <CeDropdown<CreateContentRequest>
                        label="Content Tone"
                        name="tone"
                        options={brandToneOptions}
                        InputProps={{ variant: "standard" }}
                      />
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <CeTextInput<CreateContentRequest>
                    variant="outlined"
                    name="q"
                    label={t("Describe your content")}
                    multiline
                    fullWidth
                    rows={4}
                  />
                </Grid>
              </Grid>
              <Stack direction="row-reverse" mt={2}>
                <Button type="submit" variant="contained" size="large">
                  {t("Create")}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </ThemeProvider>
    </FormProvider>
  );
};
